import { useApiTradeV1TradeCompaniesRetrieve } from 'api';
import {
  Form,
  SiteMaterialAutocomplete,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useForm,
  useResetOnChange,
} from 'form';
import { useTranslation } from 'i18n';
import { Link } from 'react-router-dom';
import { ActionRow, Button, Stack } from 'ui';

type FormValues = {
  tradeCompany: number;
  site: number;
  material: number;
};

export const SiteForm = () => {
  const { t } = useTranslation('orders');
  const form = useForm<FormValues>();

  const { watch, resetField } = form;

  const [selectedTradeCompany, selectedSite, selectedMaterial] = watch(['tradeCompany', 'site', 'material']);

  const { data: company, isLoading: isCompanyLoading } = useApiTradeV1TradeCompaniesRetrieve(
    selectedTradeCompany || 0,
    {
      query: {
        enabled: Boolean(selectedTradeCompany),
      },
    }
  );

  const disableSiteLink = !selectedTradeCompany;
  const disableMaterialLink = !selectedTradeCompany;

  useResetOnChange<FormValues>(selectedTradeCompany, ['material', 'site'], resetField);
  useResetOnChange<FormValues>(selectedSite, ['material'], resetField);

  return (
    <Stack
      sx={{
        rowGap: 5,
      }}
    >
      <Form {...form}>
        <Stack
          direction="column"
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            variant="text"
            component={Link}
            target="_blank"
            to="/ground-control/user_onboarding/"
            aria-label={t('qualitySiteForm.actions.addNewCompanyAriaLabel')}
          >
            {t('qualitySiteForm.actions.addNewCompany')}
          </Button>
          <TradeCompanyAutocomplete
            name="tradeCompany"
            label={t('qualitySiteForm.labels.company')}
            size="medium"
            required
          />
        </Stack>

        <Stack
          direction="column"
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            variant="text"
            component={Link}
            target="_blank"
            to={`/ground-control/manage_company/${company?.company_id}`}
            aria-label={t('qualitySiteForm.actions.addNewSiteAriaLabel')}
            loading={isCompanyLoading}
            disabled={disableSiteLink}
            data-testid="add-new-site-link"
          >
            {t('qualitySiteForm.actions.addNewSite')}
          </Button>
          <TradeCompanySiteSelector
            name="site"
            label={t('qualitySiteForm.labels.site')}
            size="medium"
            tradeCompanyId={selectedTradeCompany}
            required
          />
        </Stack>

        <Stack
          direction="column"
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            variant="text"
            component={Link}
            target="_blank"
            to={`/ground-control/v2/manage-company/${selectedTradeCompany}/site-materials/new`}
            aria-label={t('qualitySiteForm.actions.addNewMaterialAriaLabel')}
            loading={isCompanyLoading}
            disabled={disableMaterialLink}
            data-testid="add-new-material-link"
          >
            {t('qualitySiteForm.actions.addNewMaterial')}
          </Button>
          <SiteMaterialAutocomplete
            tradeCompanyId={selectedTradeCompany}
            tradeSite={selectedSite}
            name="material"
            label={t('qualitySiteForm.labels.material')}
            size="medium"
            required
          />
        </Stack>
      </Form>
      <ActionRow>
        <Button
          variant="primary"
          size="large"
          component={Link}
          to={`/quality/${selectedMaterial}`}
          disabled={!selectedMaterial}
          data-testid="submit-button"
        >
          {t('qualitySiteForm.actions.manage')}
        </Button>
      </ActionRow>
    </Stack>
  );
};
